import { ImagePlus, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { uploadImage } from '../lib/api';
import { useToast } from './Toaster';
import { Button } from './ui/button';

/**
 * custom 模板背景图上传控件：选择本地图片 → POST /api/upload 存 R2 →
 * 把返回的公开 URL 写回表单 logo 字段（landing 把它当全屏背景）。
 */
export function BgImageUpload({
  value,
  onChange,
}: {
  value: string;
  onChange: (url: string) => void;
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadImage(file));
      toast.success('上传成功', '背景图已更新');
    } catch (e) {
      toast.error('上传失败', e instanceof Error ? e.message : '未知错误');
    } finally {
      setUploading(false);
      // 允许重选同一文件再次触发 onChange
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img
          src={value}
          alt="背景图预览"
          className="ring-border h-14 w-10 shrink-0 rounded-lg object-cover ring-1"
        />
      ) : (
        <div className="bg-muted text-muted-foreground flex h-14 w-10 shrink-0 items-center justify-center rounded-lg text-[10px]">
          无
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      <Button
        type="button"
        variant="outline"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? (
          <>
            <Loader2 className="size-4 animate-spin" /> 上传中…
          </>
        ) : (
          <>
            <ImagePlus className="size-4" /> {value ? '更换背景图' : '上传背景图'}
          </>
        )}
      </Button>
      <span className="text-muted-foreground text-xs">png/jpg/webp，≤5MB</span>
    </div>
  );
}
